import React, { useState, useEffect } from 'react';
import { Container, Modal, Tab, Tabs, Button, Form, Table, Alert } from 'react-bootstrap';
import { API_URL } from '../../utils/constants';
import './SetupUnitsScreen.css';

function SetupUnitsScreen() {
  const [showModal, setShowModal] = useState(true);
  const [activeTab, setActiveTab] = useState('configuration');
  const [unitTypes, setUnitTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Unit Type CRUD state
  const [showUnitTypeModal, setShowUnitTypeModal] = useState(false);
  const [unitTypeModalMode, setUnitTypeModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedUnitType, setSelectedUnitType] = useState(null);
  const [unitTypeForm, setUnitTypeForm] = useState({
    unit_type: '',
    description: '',
    booking_engine: '',
    active: true,
    sort_order: 0,
    default_tax_code: ''
  });
  const [unitTypeErrors, setUnitTypeErrors] = useState({});
  const [savingUnitType, setSavingUnitType] = useState(false);
  
  // Tax codes for dropdown
  const [taxCodes, setTaxCodes] = useState([]);
  const [loadingTaxCodes, setLoadingTaxCodes] = useState(false);

  useEffect(() => {
    fetchUnitTypes();
    fetchTaxCodes();
  }, []);

  const fetchUnitTypes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/unit-types`);
      if (!response.ok) {
        throw new Error('Failed to fetch unit types');
      }
      const data = await response.json();
      setUnitTypes(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxCodes = async () => {
    try {
      setLoadingTaxCodes(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/taxes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch tax codes');
      }
      const data = await response.json();
      setTaxCodes(data);
    } catch (err) {
      console.error('Error fetching tax codes:', err);
    } finally {
      setLoadingTaxCodes(false);
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    // Modal can only be closed by clicking the X button or specific buttons
    // This prevents accidental closing
  };

  const handleModalClose = () => {
    setShowModal(false);
    setActiveTab('configuration');
  };

  const handleSaveAndClose = () => {
    // Save any changes and close modal
    setShowModal(false);
    setActiveTab('configuration');
  };

  const handleSave = () => {
    // Save any changes but keep modal open
    console.log('Saving changes...');
  };

  // Unit Type CRUD handlers
  const handleAddUnitType = () => {
    setUnitTypeModalMode('add');
    setSelectedUnitType(null);
    setUnitTypeForm({
      unit_type: '',
      description: '',
      booking_engine: '',
      active: true,
      sort_order: 0,
      default_tax_code: ''
    });
    setUnitTypeErrors({});
    setShowUnitTypeModal(true);
  };

  const handleEditUnitType = (unitType) => {
    setUnitTypeModalMode('edit');
    setSelectedUnitType(unitType);
    setUnitTypeForm({
      unit_type: unitType.unit_type,
      description: unitType.description || '',
      booking_engine: unitType.booking_engine || '',
      active: unitType.active,
      sort_order: unitType.sort_order || 0,
      default_tax_code: unitType.default_tax_code || ''
    });
    setUnitTypeErrors({});
    setShowUnitTypeModal(true);
  };

  const handleDeleteUnitType = async (unitTypeId) => {
    if (window.confirm('Are you sure you want to delete this unit type?')) {
      try {
        const response = await fetch(`${API_URL}/unit-types/${unitTypeId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to delete unit type');
        }
        
        await fetchUnitTypes(); // Refresh the list
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleUnitTypeInputChange = (field, value) => {
    setUnitTypeForm(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (unitTypeErrors[field]) {
      setUnitTypeErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateUnitTypeForm = () => {
    const errors = {};
    if (!unitTypeForm.unit_type.trim()) {
      errors.unit_type = 'Unit type is required';
    }
    if (unitTypeForm.sort_order < 0) {
      errors.sort_order = 'Sort order must be 0 or greater';
    }
    return errors;
  };

  const handleSaveUnitType = async () => {
    const errors = validateUnitTypeForm();
    if (Object.keys(errors).length > 0) {
      setUnitTypeErrors(errors);
      return;
    }

    try {
      setSavingUnitType(true);
      const url = unitTypeModalMode === 'add' 
        ? `${API_URL}/unit-types`
                  : `${API_URL}/unit-types/${selectedUnitType.id}`;
      
      const method = unitTypeModalMode === 'add' ? 'POST' : 'PUT';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(unitTypeForm)
      });

      if (!response.ok) {
        throw new Error('Failed to save unit type');
      }

      await fetchUnitTypes(); // Refresh the list
      setShowUnitTypeModal(false);
      setUnitTypeForm({
        unit_type: '',
        description: '',
        booking_engine: '',
        active: true,
        sort_order: 0
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingUnitType(false);
    }
  };

  const handleCloseUnitTypeModal = () => {
    setShowUnitTypeModal(false);
    setUnitTypeForm({
      unit_type: '',
      description: '',
      booking_engine: '',
      active: true,
      sort_order: 0
    });
    setUnitTypeErrors({});
  };

  return (
    <Container fluid className="setup-units-screen">
      <div className="screen-header">
        <h1>Setup - Units</h1>
        <p>Configure unit settings and manage unit types</p>
      </div>

      <div className="screen-content">
        <div className="action-buttons">
          <Button 
            variant="primary" 
            onClick={handleOpenModal}
            className="setup-button"
          >
            🏠 Open Setup
          </Button>
        </div>

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {/* Main Setup Modal */}
        <Modal 
          show={showModal} 
          onHide={handleCloseModal}
          size="xl"
          backdrop="static"
          keyboard={false}
          className="setup-modal"
        >
          <Modal.Header>
            <Modal.Title>Setup - Units</Modal.Title>
                         <Button
               variant="link"
               onClick={handleModalClose}
               className="close-button"
             >
               ×
             </Button>
          </Modal.Header>
          
          <Modal.Body>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="setup-tabs"
            >
              <Tab eventKey="configuration" title="Configuration">
                <div className="tab-content">
                  <h4>Configuration</h4>
                  <p>Configuration options will be added here in the future.</p>
                </div>
              </Tab>
              
              <Tab eventKey="unit-type" title="Unit Type">
                <div className="tab-content">
                  <div className="tab-header">
                    <h4>Unit Types</h4>
                    <Button 
                      variant="success" 
                      onClick={handleAddUnitType}
                      size="sm"
                    >
                      ➕ Add Unit Type
                    </Button>
                  </div>
                  
                  {loading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Unit Type</th>
                            <th>Description</th>
                            <th>Booking Engine</th>
                            <th>Default Tax Code</th>
                            <th>Active</th>
                            <th>Sort Order</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {unitTypes.map((unitType) => (
                            <tr key={unitType.id}>
                              <td>{unitType.unit_type}</td>
                              <td>{unitType.description || '-'}</td>
                              <td>{unitType.booking_engine || '-'}</td>
                              <td>{unitType.default_tax_code || '-'}</td>
                              <td>
                                <span className={`badge ${unitType.active ? 'bg-success' : 'bg-secondary'}`}>
                                  {unitType.active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>{unitType.sort_order}</td>
                              <td>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditUnitType(unitType)}
                                  className="me-2"
                                >
                                  ✏️ Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteUnitType(unitType.id)}
                                >
                                  🗑️ Delete
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>
          </Modal.Body>
          
          <Modal.Footer>
            <Button onClick={handleSaveAndClose}>
              Save & Close
            </Button>
            <Button onClick={handleSave}>
              Save
            </Button>
            <Button onClick={handleModalClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Unit Type Modal */}
        <Modal 
          show={showUnitTypeModal} 
          onHide={handleCloseUnitTypeModal}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {unitTypeModalMode === 'add' ? 'Add Unit Type' : 'Edit Unit Type'}
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Unit Type *</Form.Label>
                <Form.Control
                  type="text"
                  value={unitTypeForm.unit_type}
                  onChange={(e) => handleUnitTypeInputChange('unit_type', e.target.value)}
                  isInvalid={!!unitTypeErrors.unit_type}
                  placeholder="e.g., Studio, 1 Bedroom, 2 Bedroom"
                />
                <Form.Control.Feedback type="invalid">
                  {unitTypeErrors.unit_type}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={unitTypeForm.description}
                  onChange={(e) => handleUnitTypeInputChange('description', e.target.value)}
                  placeholder="Description of the unit type"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Booking Engine</Form.Label>
                <Form.Control
                  type="text"
                  value={unitTypeForm.booking_engine}
                  onChange={(e) => handleUnitTypeInputChange('booking_engine', e.target.value)}
                  placeholder="e.g., Booking.com, Airbnb, VRBO"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Default Tax Code</Form.Label>
                <Form.Select
                  value={unitTypeForm.default_tax_code}
                  onChange={(e) => handleUnitTypeInputChange('default_tax_code', e.target.value)}
                  disabled={loadingTaxCodes}
                >
                  <option value="">Select a tax code (optional)</option>
                  {taxCodes.map((tax) => (
                    <option key={tax.id} value={tax.tax_code}>
                      {tax.tax_code} - {tax.description || 'No description'}
                    </option>
                  ))}
                </Form.Select>
                {loadingTaxCodes && (
                  <Form.Text className="text-muted">
                    Loading tax codes...
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Sort Order</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={unitTypeForm.sort_order}
                  onChange={(e) => handleUnitTypeInputChange('sort_order', parseInt(e.target.value) || 0)}
                  isInvalid={!!unitTypeErrors.sort_order}
                />
                <Form.Control.Feedback type="invalid">
                  {unitTypeErrors.sort_order}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Active"
                  checked={unitTypeForm.active}
                  onChange={(e) => handleUnitTypeInputChange('active', e.target.checked)}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseUnitTypeModal}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveUnitType}
              disabled={savingUnitType}
            >
              {savingUnitType ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  );
}

export default SetupUnitsScreen;
